import { useEffect } from "react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { useLanguage } from "@/context/LanguageContext";

const CONTENT = {
  en: {
    title: "Privacy Policy",
    updated: "Last updated: August 3, 2026",
    sections: [
      {
        h: "Who we are",
        p: [
          "Dent Xpress is a paintless dent repair (PDR) workshop located in Shuwaikh Industrial, Kuwait. You can reach us any time on WhatsApp at +965 55787747.",
        ],
      },
      {
        h: "What we collect",
        p: ["We only collect what you choose to give us, plus basic usage statistics:"],
        list: [
          "Contact details and vehicle/dent photos you voluntarily send us on WhatsApp.",
          "Quote form submissions on this site: your name, phone number, and any car details, dent description, or photos you include.",
          "Technical analytics data: which pages you visit, your device and browser type, and where your visit came from (for example a Snapchat, Google, or Facebook/Instagram ad).",
        ],
      },
      {
        h: "Analytics & advertising tools we use",
        p: [
          "To understand how the site is used and how our ads perform, we use Google Analytics 4, the Meta Pixel and Meta Conversions API (Facebook/Instagram), and Google Ads click tracking (gclid).",
          "These tools measure site usage and advertising performance. The data they collect is shared with Google and Meta for those purposes only.",
        ],
      },
      {
        h: "Cookies",
        p: [
          "The analytics and advertising tools above use cookies and similar technologies (such as browser storage) to do their job. You can block or delete cookies in your browser settings at any time.",
        ],
      },
      {
        h: "What we don't do",
        p: [
          "We do not sell your personal data. We do not share it with anyone beyond the service and analytics providers named above (Google and Meta).",
        ],
      },
      {
        h: "How long we keep your data",
        p: [
          "We keep enquiry data (names, phone numbers, photos) only as long as needed to give you a quote and provide our service.",
        ],
      },
      {
        h: "Your rights",
        p: [
          "You can ask us what data we hold about you, or request that we delete it, by messaging us on WhatsApp at 55787747.",
        ],
      },
    ],
  },
  ar: {
    title: "سياسة الخصوصية",
    updated: "آخر تحديث: ٣ أغسطس ٢٠٢٦",
    sections: [
      {
        h: "من نحن",
        p: [
          "دنت إكسبرس ورشة متخصصة في إزالة الضربات بدون صبغ (سمكرة بدون بوية) في الشويخ الصناعية، الكويت. تقدر تتواصل معنا في أي وقت على الواتساب: ‎+965 55787747.",
        ],
      },
      {
        h: "البيانات اللي نجمعها",
        p: ["نجمع فقط اللي تختار ترسله لنا، إضافة إلى إحصائيات استخدام أساسية:"],
        list: [
          "بيانات التواصل وصور السيارة/الضربة اللي ترسلها لنا بنفسك على الواتساب.",
          "طلبات التسعير من الموقع: اسمك ورقم تلفونك، وأي تفاصيل عن السيارة أو وصف الضربة أو صور تضيفها.",
          "بيانات تحليلية تقنية: الصفحات اللي تزورها، نوع جهازك ومتصفحك، ومصدر زيارتك (مثلاً إعلان سناب شات أو قوقل أو فيسبوك/انستقرام).",
        ],
      },
      {
        h: "أدوات التحليل والإعلان اللي نستخدمها",
        p: [
          "لفهم استخدام الموقع وقياس أداء إعلاناتنا، نستخدم Google Analytics 4 وMeta Pixel وMeta Conversions API (فيسبوك/انستقرام) وتتبع نقرات إعلانات قوقل (gclid).",
          "هذي الأدوات تقيس استخدام الموقع وأداء الإعلانات فقط، والبيانات اللي تجمعها تُشارك مع قوقل وميتا لهذه الأغراض حصراً.",
        ],
      },
      {
        h: "ملفات تعريف الارتباط (الكوكيز)",
        p: [
          "أدوات التحليل والإعلان المذكورة تستخدم الكوكيز وتقنيات مشابهة (مثل تخزين المتصفح) لأداء عملها. تقدر تحظر أو تحذف الكوكيز من إعدادات متصفحك في أي وقت.",
        ],
      },
      {
        h: "اللي ما نسويه",
        p: [
          "ما نبيع بياناتك الشخصية أبداً، وما نشاركها مع أي جهة غير مزودي الخدمة والتحليل المذكورين أعلاه (قوقل وميتا).",
        ],
      },
      {
        h: "مدة الاحتفاظ بالبيانات",
        p: [
          "نحتفظ ببيانات الاستفسارات (الأسماء وأرقام التلفون والصور) فقط للمدة اللازمة لتزويدك بالسعر وتقديم الخدمة.",
        ],
      },
      {
        h: "حقوقك",
        p: [
          "تقدر تسألنا عن البيانات اللي عندنا عنك، أو تطلب حذفها، برسالة واتساب على 55787747.",
        ],
      },
    ],
  },
};

export default function Privacy() {
  const { lang } = useLanguage();
  const c = CONTENT[lang];

  useEffect(() => {
    const prev = document.title;
    document.title =
      lang === "ar" ? "سياسة الخصوصية — دنت إكسبرس" : "Privacy Policy — Dent Xpress";
    window.scrollTo(0, 0);
    return () => { document.title = prev; };
  }, [lang]);

  return (
    <main className="min-h-screen bg-[#0a0a0a] text-white" data-testid="privacy-page">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight" data-testid="privacy-title">
          {c.title}
        </h1>
        <p className="mt-3 text-sm text-white/50" data-testid="privacy-updated">{c.updated}</p>
        <div className="mt-4 w-12 h-1 rounded-full bg-[#e10600]" />

        {c.sections.map((s, i) => (
          <section key={i} className="mt-10" data-testid={`privacy-section-${i}`}>
            <h2 className="text-lg font-bold text-white">{s.h}</h2>
            {s.p.map((para, j) => (
              <p key={j} className="mt-3 text-white/70 leading-relaxed">{para}</p>
            ))}
            {s.list && (
              <ul className="mt-3 space-y-2 ps-1">
                {s.list.map((item, j) => (
                  <li key={j} className="flex gap-3 text-white/70 leading-relaxed">
                    <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#e10600] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
      <Footer />
    </main>
  );
}
